import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SyncDashboard = ({ tenantId, syncStatus, onManualSync }) => {
  const [syncMetrics, setSyncMetrics] = useState({
    totalSyncs: 0,
    successfulSyncs: 0,
    failedSyncs: 0,
    avgSyncTime: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tenantId) {
      loadSyncMetrics();
      loadRecentActivity();
    }
  }, [tenantId]);

  const loadSyncMetrics = async () => {
    try {
      const { data, error } = await supabase
        ?.from('pos_inventory_logs')
        ?.select('*')
        ?.eq('tenant_id', tenantId)
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (error) throw error;

      setSyncMetrics({
        totalSyncs: data?.length || 0,
        successfulSyncs: data?.filter(log => log?.reason !== 'error')?.length || 0,
        failedSyncs: data?.filter(log => log?.reason === 'error')?.length || 0,
        avgSyncTime: 1.2
      });
    } catch (error) {
      console.error('Load sync metrics error:', error);
    }
  };

  const loadRecentActivity = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('pos_inventory_logs')
        ?.select('*, pos_products(name, sku)')
        ?.eq('tenant_id', tenantId)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (error) throw error;
      setRecentActivity(data || []);
    } catch (error) {
      console.error('Load recent activity error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSyncHealthColor = () => {
    switch (syncStatus?.syncHealth) {
      case 'healthy':
        return 'text-success';
      case 'syncing':
        return 'text-primary';
      case 'error':
        return 'text-danger';
      default:
        return 'text-muted-foreground';
    }
  };

  const getReasonIcon = (reason) => {
    switch (reason) {
      case 'sale':
        return 'ShoppingCart';
      case 'restock':
        return 'PackagePlus';
      case 'adjustment':
        return 'Edit';
      case 'bulk_import':
        return 'Upload';
      case 'return':
        return 'Undo';
      default:
        return 'Activity';
    }
  };

  return (
    <div className="space-y-6">
      {/* Sync Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg p-6 border border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Sync Health</span>
            <Icon name="Heart" size={20} className={getSyncHealthColor()} />
          </div>
          <p className={`text-2xl font-bold ${getSyncHealthColor()}`}>
            {syncStatus?.syncHealth?.charAt(0)?.toUpperCase() + syncStatus?.syncHealth?.slice(1)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Last sync: {syncStatus?.lastSync?.toLocaleTimeString()}
          </p>
        </div>

        <div className="bg-card rounded-lg p-6 border border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Total Syncs</span>
            <Icon name="RefreshCw" size={20} className="text-primary" />
          </div>
          <p className="text-2xl font-bold text-foreground">{syncMetrics?.totalSyncs}</p>
          <p className="text-xs text-muted-foreground mt-1">All time operations</p>
        </div>

        <div className="bg-card rounded-lg p-6 border border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Success Rate</span>
            <Icon name="CheckCircle" size={20} className="text-success" />
          </div>
          <p className="text-2xl font-bold text-success">
            {syncMetrics?.totalSyncs > 0
              ? Math.round((syncMetrics?.successfulSyncs / syncMetrics?.totalSyncs) * 100)
              : 0}%
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {syncMetrics?.successfulSyncs} successful
          </p>
        </div>

        <div className="bg-card rounded-lg p-6 border border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Pending Updates</span>
            <Icon name="Clock" size={20} className="text-warning" />
          </div>
          <p className="text-2xl font-bold text-warning">{syncStatus?.pendingUpdates || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Awaiting sync</p>
        </div>
      </div>

      {/* Recent Sync Activity */}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Recent Sync Activity</h3>
            <Button variant="outline" size="sm" onClick={loadRecentActivity}>
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Refresh
            </Button>
          </div>
        </div>
        <div className="divide-y divide-border">
          {loading ? (
            <div className="p-8 text-center">
              <Icon name="Loader2" size={32} className="text-primary animate-spin mx-auto mb-2" />
              <p className="text-muted-foreground">Loading activity...</p>
            </div>
          ) : recentActivity?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="Activity" size={48} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No recent sync activity</p>
            </div>
          ) : (
            recentActivity?.map((activity) => (
              <div key={activity?.id} className="p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon name={getReasonIcon(activity?.reason)} size={20} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-medium text-foreground">
                        {activity?.pos_products?.name || 'Unknown Product'}
                      </h4>
                      <span className="text-xs text-muted-foreground">
                        {new Date(activity?.created_at)?.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      SKU: {activity?.pos_products?.sku || 'N/A'}
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">
                        Reason: <span className="font-medium text-foreground">{activity?.reason}</span>
                      </span>
                      <span className="text-muted-foreground">
                        Change: <span className={`font-medium ${
                          activity?.quantity_change > 0 ? 'text-success' : 'text-danger'
                        }`}>
                          {activity?.quantity_change > 0 ? '+' : ''}{activity?.quantity_change}
                        </span>
                      </span>
                      <span className="text-muted-foreground">
                        Stock: {activity?.quantity_before} → {activity?.quantity_after}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SyncDashboard;